import React, {Component} from 'react';
import ReactTable from 'react-table';
import {baseTokenSymbol, fetchIntervalTime, reactTableText} from '../../constants/configs';
import {keyBy, mapValues, omit} from 'lodash';
import Api from '../../services/Api';
import moment from 'moment';
import {
  i18nizeDate,
  numberWithCommas,
  mapStatus,
  getStatusColor
} from '../../helpers/Utils';
import {withTranslation} from "react-i18next";
import ToolTip from '../../components/ToolTip';
import {NotificationManager} from "../../components/common/react-notifications";
import ReactTableTextFilter from "../../components/ReactTableTextFilter";
import ModalManager from "../../components/modal/ModalManager";
import BankDepositFeedbackForm from "../BankDepositFeedbackForm";
import ReactTableSelectFilter from "../../components/ReactTableSelectFilter";
import ReactTableDateFilter from "../../components/ReactTableDateFilter";
import BankPaymentDetails from "../BankPaymentDetails";
import Zoom from "react-medium-image-zoom";

class BankDeposits extends Component {
  constructor(props) {
    super(props);
    this.state = {
      deposits: [],
      pages: -1,
      loading: false,
      selectedStartDate: null,
      selectedEndDate: null,
      expanded: {},
    };
    this.renderImageCell = this.renderImageCell.bind(this);
    this.renderStatusFilter = this.renderStatusFilter.bind(this);
    this.renderDateFilter = this.renderDateFilter.bind(this);
  }

  async componentDidMount() {
    this.interval = setInterval(async () => {await this.fetchDeposits(this.table.state);}, fetchIntervalTime);
  }

  componentWillUnmount() {
    clearInterval(this.interval);
  }

  fetchData = async state => {
    this.setState({loading: true});
    await this.fetchDeposits(state);
    this.setState({loading: false});
  };

  fetchDeposits = async state => {
    try {
      const {userId} = this.props;
      const offset = state.page * state.pageSize;
      const {data} = await Api.invoke(
        'GET',
        '/v2/admin-ui/deposits/bankPayments',
        null,
        {
          params: {
            offset,
            limit: state.pageSize,
            ...(userId ? {userId} : null),
            ...(state.filtered.length ? omit(mapValues(keyBy(state.filtered, 'id'), 'value'), 'createdAt') : null),
            ...(this.state.selectedStartDate ? {from: moment(this.state.selectedStartDate).toISOString()} : null),
            ...(this.state.selectedEndDate ? {to: moment(this.state.selectedEndDate).toISOString()} : null),
          },
        },
      );
      this.setState({
        deposits: data.rows,
        pages: Math.ceil(data.count / state.pageSize),
      });
    } catch (e) {
      console.log('error: ', e);
    }
  };

  confirmPayment = async paymentId => {
    const {t} = this.props;
    try {
      await Api.invoke('PATCH', `/v2/admin-ui/deposits/bankPayments/${paymentId}`, {
        status: 'CONFIRMED',
      });
      NotificationManager.success(t('Bank payment confirmed successfully'), t('Message'));
      this.fetchDeposits(this.table.state);
    } catch (e) {
      console.log("error: ", e);
    }
  };

  getColumns = t => [
    {
      Header: t('Payment Code'),
      accessor: 'receiptCode',
      filterable: true,
      Filter: ReactTableTextFilter,
    },
    {
      Header: t('Slip Image'),
      accessor: 'receipt',
      Cell: this.renderImageCell,
    },
    {
      Header: `${t('Amount')} (${t(baseTokenSymbol)})`,
      accessor: 'amount',
      Cell: props => <span>{numberWithCommas(Number(props.value))}</span>,
    },
    {
      Header: t('Status'),
      accessor: 'status',
      filterable: true,
      Filter: this.renderStatusFilter,
      Cell: props => <span className={getStatusColor(props.value)}>{mapStatus(props.value)}</span>,
    },
    {
      Header: t('Date'),
      accessor: 'createdAt',
      width: 200,
      Cell: props => <span>{i18nizeDate(props.value)}</span>,
      filterable: true,
      Filter: this.renderDateFilter,
    },
    {
      Header: t('Actions'),
      accessor: 'id',
      width: 80,
      Cell: props =>
        <div className='d-flex flex-row align-items-center justify-content-around'>
          <ToolTip id={`confirm_${props.original.userId}`} text={t('Confirm Deposit')}>
            <i className="icon-Yes text-success text-one font-weight-bold cursor-pointer" onClick={() => {this.onPressConfirm(props.value);}}/>
          </ToolTip>
          <ToolTip id={`reject_${props.original.userId}`} text={t('Reject Deposit')}>
            <i className="icon-Close text-danger text-one font-weight-bold cursor-pointer"
               onClick={() => {this.showBankDepositFeedbackForm('SUBMIT', props.original);}}/>
          </ToolTip>
        </div>,
    },
  ];

  onPressConfirm = paymentId => {
    const {t} = this.props;
    ModalManager.open({
      isOpen: true,
      title: t('Confirm Deposit'),
      body: t('Are you sure you want to confirm this payment ?'),
      buttons: [
        {
          color: 'primary',
          onClick: async () => {
            await this.confirmPayment(paymentId);
            ModalManager.close({
              isOpen: false
            });
          },
          text: t('Accept'),
        },
        {
          color: 'grey',
          onClick: () => {
            ModalManager.close({
              isOpen: false
            });
          },
          text: t('Close'),
        },
      ],
    });
  };

  showBankDepositFeedbackForm = (type, transaction) => {
    const {t} = this.props;
    const {id, rejectReason} = transaction;
    ModalManager.open({
      isOpen: true,
      title: t('Rejection Reason'),
      body: <BankDepositFeedbackForm
        type={type}
        paymentId={id}
        rejectReason={rejectReason}
        afterSubmit={async () => {await this.fetchDeposits(this.table.state);}}
      />,
    });
  };

  renderStatusFilter({filter, onChange}) {
    const {t} = this.props;
    const options = [
      {value: '', label: t('all')},
      {value: 'SUBMITTED', label: t('Submitted')},
      {value: 'CONFIRMED', label: t('Confirmed')},
      {value: 'REJECTED', label: t('Rejected')},
    ];
    return <ReactTableSelectFilter
      filter={filter}
      onChange={onChange}
      options={options}
    />;
  }

  renderImageCell(props) {
    if (props.value) {
      const {id} = props.original;
      return (
        <div className='d-flex flex-row align-items-center justify-content-around'>
          <div key={id}>
            <img
              onClick={() => {
                const {firstName, lastName} = props.original.user;
                ModalManager.open({
                  size: 'lg',
                  isOpen: true,
                  title: `${firstName} ${lastName}`,
                  body:
                    <Zoom><img src={props.value} width="100%" height="100%"/></Zoom>,
                });
              }}
              src={props.value}
              height={40}
              width={40}
            />
          </div>
        </div>
      );
    }
    return null;
  }

  renderDateFilter({onChange}) {
    const {selectedStartDate, selectedEndDate} = this.state;
    return <ReactTableDateFilter
      onChange={onChange}
      handleDateChange={this.handleDateChange}
      selectedStartDate={selectedStartDate}
      selectedEndDate={selectedEndDate}
    />;
  }

  handleDateChange = (type, date) => {
    this.setState({
      [type]: date,
    });
  };

  onExpanded = (newExpanded, index) => {
    this.setState({
      expanded: {
        ...this.state.expanded,
        [index[0]]: !!newExpanded[index[0]],
      },
    });
  };

  render() {
    const {t} = this.props;
    const {deposits, pages, loading, expanded} = this.state;
    return (
      <ReactTable
        manual
        ref={el => {this.table = el;}}
        className="-highlight"
        data={deposits}
        pages={pages}
        loading={loading}
        sortable={false}
        resizable={true}
        expanded={expanded}
        onFetchData={this.fetchData}
        columns={this.getColumns(t)}
        onExpandedChange={this.onExpanded}
        style={{width: '100%', marginTop: 20}}
        onPageChange={() => {this.setState({expanded: {}});}}
        SubComponent={({original}) =>
          <BankPaymentDetails
            transaction={original}
            showBankDepositFeedbackForm={this.showBankDepositFeedbackForm}
          />
        }
        defaultPageSize={10}
        {...reactTableText}
      />
    );
  }
}

export default withTranslation()(BankDeposits);
